import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { feature as topoFeature } from 'topojson-client'
import type { Topology, GeometryCollection } from 'topojson-specification'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import { MapPin, AlertTriangle, Users } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { apiFetch } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { normaliseCountryName } from '@/lib/world-atlas-aliases'
import { makeCountIcon } from '@/pages/territory-map-helpers'

const WORLD_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

interface CountryFeatureProps {
  name: string
}

// ── Types ────────────────────────────────────────────────────────────────────
type TerritoryKind = 'continent' | 'country' | 'subdivision' | 'locality'

interface Territory {
  id: string
  name: string
  kind: TerritoryKind
  parentId: string | null
  isoCode: string | null
  centroidLat?: number | null
  centroidLng?: number | null
  defaultRadiusKm?: number | null
}

interface BrokerLite {
  id: string
  name: string
  company: string | null
  tier: string
  parentId: string | null
}

interface Assignment {
  id: string
  brokerId: string
  territoryId: string | null
  radiusCenterLat: number | null
  radiusCenterLng: number | null
  radiusKm: number | null
  radiusLabel: string | null
  radiusKmOverride: number | null
  isPrimary: boolean
  contactCompany: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  contactWebsite: string | null
  notes: string | null
  territory?: (Territory & { parent?: Territory | null }) | null
}

interface CoveragePayload {
  brokers: BrokerLite[]
  assignments: Assignment[]
  childrenOf: Record<string, string[]>
  activityByCountry?: Record<string, CountryActivity>
  fanDemandPins?: FanDemandPinLite[]
}

interface CountryActivity {
  confirmedShows: number
  ticketsSold: number
  activeInquiries: number
  fanRequests: number
}

interface FanDemandPinLite {
  cityKey: string
  city: string
  country: string | null
  lat: number
  lng: number
  requestCount: number
  sampleComments: Array<{ text: string; commenterName: string | null }>
}

type MapPaintMode = 'responsibility' | 'selling' | 'fan_gap'

type CountryStatus = 'selling' | 'pipeline' | 'fan_gap' | 'covered_idle' | 'idle'

const FAN_PIN_COLOUR = '#6366f1'

const STATUS_FILL: Record<CountryStatus, string> = {
  selling: '#059669',
  pipeline: '#fbbf24',
  fan_gap: '#fca5a5',
  covered_idle: '#a7f3d0',
  idle: '#f3f4f6',
}

function lookupActivity(
  activityByCountry: Record<string, CountryActivity> | undefined,
  countryName: string,
): CountryActivity | undefined {
  if (!activityByCountry) return undefined
  const keys = [
    countryName.trim().toLowerCase(),
    normaliseCountryName(countryName).trim().toLowerCase(),
  ]
  for (const k of keys) {
    if (activityByCountry[k]) return activityByCountry[k]
  }
  return undefined
}

function deriveCountryStatus(
  brokerCount: number,
  activity?: CountryActivity,
): CountryStatus {
  if (activity && (activity.confirmedShows > 0 || activity.ticketsSold > 0)) return 'selling'
  if (activity && activity.activeInquiries > 0) return 'pipeline'
  if (activity && activity.fanRequests > 0 && brokerCount === 0) return 'fan_gap'
  if (brokerCount > 0) return 'covered_idle'
  return 'idle'
}

function describeTerritoryAssignment(a: Assignment): string {
  if (a.territory) return a.territory.name
  if (a.radiusLabel) return a.radiusLabel
  if (a.radiusKm != null) return `${a.radiusKm}km radius`
  return 'Territory'
}

const TIER_COLOUR: Record<string, string> = {
  primary: 'bg-blue-100 text-blue-800',
  secondary: 'bg-indigo-100 text-indigo-800',
  sub_broker: 'bg-violet-100 text-violet-800',
  independent: 'bg-slate-100 text-slate-800',
  regional: 'bg-teal-100 text-teal-800',
  specialist: 'bg-amber-100 text-amber-800',
  co_promoter: 'bg-pink-100 text-pink-800',
  exclusive: 'bg-emerald-100 text-emerald-800',
}

// ── Section (embedded in BrokersPage) ───────────────────────────────────────
interface SectionProps {
  focusedBrokerId?: string | null
  onClearFocus?: () => void
  onFocusBroker?: (brokerId: string) => void
}

export function BrokerCoverageSection({ focusedBrokerId, onClearFocus, onFocusBroker }: SectionProps = {}) {
  const [gapsOnly, setGapsOnly] = useState(false)
  const [rollUp, setRollUp] = useState(true)
  const [mapMode, setMapMode] = useState<MapPaintMode>('selling')
  const [showFanOverlay, setShowFanOverlay] = useState(true)

  const coverageQ = useQuery<CoveragePayload, ApiClientError>({
    queryKey: ['broker-coverage'],
    queryFn: () => artistFetch<CoveragePayload>('/brokers/coverage'),
  })
  const continentsQ = useQuery<Territory[]>({
    queryKey: ['territories', 'continents'],
    queryFn: () => apiFetch<Territory[]>('/territories?kind=continent'),
  })

  return (
    <section className="mt-10 border-t border-ovation-ink/10 pt-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl">Ticket agents & territory map</h2>
          <p className="text-sm text-ovation-muted">
            See which agents cover which areas, sub-agents under each primary, where tickets are
            selling, and where fans are asking for shows without an agent.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-ovation-muted">Map view</span>
            <select
              value={mapMode}
              onChange={(e) => setMapMode(e.target.value as MapPaintMode)}
              className="rounded-md border border-ovation-ink/15 px-2 py-1 text-sm"
            >
              <option value="selling">Selling activity</option>
              <option value="fan_gap">Fan gaps (no agent)</option>
              <option value="responsibility">Agent responsibility</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showFanOverlay}
              onChange={(e) => setShowFanOverlay(e.target.checked)}
            />
            Fan locations
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={rollUp} onChange={(e) => setRollUp(e.target.checked)} />
            Roll up sub-agents
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={gapsOnly}
              onChange={(e) => setGapsOnly(e.target.checked)}
            />
            Gaps only
          </label>
        </div>
      </div>

      <AsyncBoundary
        isLoading={coverageQ.isLoading || continentsQ.isLoading}
        error={coverageQ.error}
      >
        {coverageQ.data && continentsQ.data && (
          <CoverageBody
            coverage={coverageQ.data}
            continents={continentsQ.data}
            gapsOnly={gapsOnly}
            rollUp={rollUp}
            mapMode={mapMode}
            showFanOverlay={showFanOverlay}
            focusedBrokerId={focusedBrokerId ?? null}
            {...(onClearFocus ? { onClearFocus } : {})}
            {...(onFocusBroker ? { onFocusBroker } : {})}
          />
        )}
      </AsyncBoundary>
    </section>
  )
}

// ── Roll-up logic ───────────────────────────────────────────────────────────
function buildDescendantClosure(childrenOf: Record<string, string[]>) {
  const memo: Record<string, Set<string>> = {}
  function descendants(brokerId: string): Set<string> {
    if (memo[brokerId]) return memo[brokerId]
    const acc = new Set<string>([brokerId])
    for (const child of childrenOf[brokerId] ?? []) {
      for (const d of descendants(child)) acc.add(d)
    }
    memo[brokerId] = acc
    return acc
  }
  return descendants
}

interface AssignmentsByTerritory {
  // continent id -> [{ broker, viaBrokerId? }]
  continents: Record<string, BrokerBadge[]>
  // country id -> [...]
  countries: Record<string, BrokerBadge[]>
  // unknown / orphan radius assignments per broker
  radius: Record<string, Assignment[]>
}

interface BrokerBadge {
  broker: BrokerLite
  viaBrokerId?: string  // set if this assignment came from a descendant
}

function rollupAssignments(
  coverage: CoveragePayload,
  rollUp: boolean,
): AssignmentsByTerritory {
  const result: AssignmentsByTerritory = { continents: {}, countries: {}, radius: {} }
  const brokerById = new Map(coverage.brokers.map((b) => [b.id, b]))
  const descendants = rollUp ? buildDescendantClosure(coverage.childrenOf) : null

  for (const b of coverage.brokers) {
    const ownAssignmentBrokerIds = rollUp ? [...(descendants!(b.id))] : [b.id]
    for (const otherId of ownAssignmentBrokerIds) {
      for (const a of coverage.assignments.filter((x) => x.brokerId === otherId)) {
        const badge: BrokerBadge =
          otherId !== b.id ? { broker: b, viaBrokerId: otherId } : { broker: b }
        // Walk up the territory chain to find continent + country
        if (a.territory) {
          const continentId = findAncestorByKind(a.territory, 'continent')
          const countryId = findAncestorByKind(a.territory, 'country')
          if (continentId) {
            const list = result.continents[continentId] ?? []
            if (!list.some((x) => x.broker.id === badge.broker.id && x.viaBrokerId === badge.viaBrokerId)) {
              list.push(badge)
              result.continents[continentId] = list
            }
          }
          if (countryId) {
            const list = result.countries[countryId] ?? []
            if (!list.some((x) => x.broker.id === badge.broker.id && x.viaBrokerId === badge.viaBrokerId)) {
              list.push(badge)
              result.countries[countryId] = list
            }
          }
        } else if (a.radiusCenterLat != null) {
          const list = result.radius[b.id] ?? []
          list.push(a)
          result.radius[b.id] = list
        }
      }
    }
    // Direct radius assignments
    if (!rollUp) {
      for (const a of coverage.assignments.filter((x) => x.brokerId === b.id && !x.territoryId)) {
        const list = result.radius[b.id] ?? []
        list.push(a)
        result.radius[b.id] = list
      }
    }
  }

  // Helper used above; closed over locals — define inline
  function findAncestorByKind(t: Territory & { parent?: Territory | null }, kind: TerritoryKind): string | undefined {
    let node: (Territory & { parent?: Territory | null }) | null | undefined = t
    while (node) {
      if (node.kind === kind) return node.id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      node = (node as any).parent ?? null
    }
    return undefined
  }

  brokerById // suppress unused
  return result
}

// ── Map paint: smallest-area-only ──────────────────────────────────────────
// For each broker, find their country-level "leaves":
//  - A direct country assignment paints that country (unless a sub-country
//    assignment of the same broker shadows it).
//  - A continent assignment paints every country in that continent (unless
//    one of them is directly assigned or has a sub-country assignment).
//  - Subdivision/locality assignments are rendered separately as circles.
function computeCountryLeafPaint(
  coverage: CoveragePayload,
  rollUp: boolean,
  allCountries: Territory[],
): Record<string, BrokerBadge[]> {
  const result: Record<string, BrokerBadge[]> = {}
  const descendants = rollUp ? buildDescendantClosure(coverage.childrenOf) : null

  // Country -> continentId index for continent-level expansion
  const continentOfCountry = new Map<string, string | null>()
  for (const c of allCountries) continentOfCountry.set(c.id, c.parentId)
  const countriesByContinent = new Map<string, Territory[]>()
  for (const c of allCountries) {
    if (!c.parentId) continue
    const list = countriesByContinent.get(c.parentId) ?? []
    list.push(c)
    countriesByContinent.set(c.parentId, list)
  }

  function isDescendantOf(t: Territory & { parent?: Territory | null }, ancestorId: string): boolean {
    let p: (Territory & { parent?: Territory | null }) | null | undefined = t.parent
    while (p) {
      if (p.id === ancestorId) return true
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      p = (p as any).parent ?? null
    }
    return false
  }

  function addBadge(countryId: string, broker: BrokerLite, viaBrokerId: string | undefined) {
    const badge: BrokerBadge = viaBrokerId ? { broker, viaBrokerId } : { broker }
    const list = result[countryId] ?? []
    if (!list.some((x) => x.broker.id === badge.broker.id && x.viaBrokerId === badge.viaBrokerId)) {
      list.push(badge)
      result[countryId] = list
    }
  }

  for (const broker of coverage.brokers) {
    const sourceBrokerIds = rollUp ? [...descendants!(broker.id)] : [broker.id]
    const mine = coverage.assignments.filter(
      (a) => sourceBrokerIds.includes(a.brokerId) && a.territory,
    )

    for (const a of mine) {
      if (!a.territory) continue
      const via = a.brokerId !== broker.id ? a.brokerId : undefined

      if (a.territory.kind === 'country') {
        // Shadowed by any sub-country (subdivision/locality) of the same broker?
        const shadowed = mine.some((other) => {
          if (other.id === a.id || !other.territory) return false
          return isDescendantOf(other.territory, a.territory!.id)
        })
        if (shadowed) continue
        addBadge(a.territory.id, broker, via)
      } else if (a.territory.kind === 'continent') {
        const children = countriesByContinent.get(a.territory.id) ?? []
        for (const country of children) {
          // Shadowed if the same broker has the country directly or any descendant.
          const shadowed = mine.some((other) => {
            if (other.id === a.id || !other.territory) return false
            if (other.territoryId === country.id) return true
            return isDescendantOf(other.territory, country.id)
          })
          if (shadowed) continue
          addBadge(country.id, broker, via)
        }
      }
    }
  }
  return result
}

// ── Body ────────────────────────────────────────────────────────────────────
function CoverageBody({
  coverage,
  continents,
  gapsOnly,
  rollUp,
  mapMode,
  showFanOverlay,
  focusedBrokerId,
  onClearFocus,
  onFocusBroker,
}: {
  coverage: CoveragePayload
  continents: Territory[]
  gapsOnly: boolean
  rollUp: boolean
  mapMode: MapPaintMode
  showFanOverlay: boolean
  focusedBrokerId: string | null
  onClearFocus?: () => void
  onFocusBroker?: (brokerId: string) => void
}) {
  // When focused on a single broker, narrow the coverage payload to just that
  // broker (plus its descendants when the rollup toggle is on). Both the map
  // paint and the radius layer derive from this filtered payload.
  const focusedCoverage = useMemo<CoveragePayload>(() => {
    if (!focusedBrokerId) return coverage
    const descendants = rollUp ? buildDescendantClosure(coverage.childrenOf) : null
    const visibleBrokerIds = descendants
      ? descendants(focusedBrokerId)
      : new Set([focusedBrokerId])
    return {
      ...coverage,
      brokers: coverage.brokers.filter((b) => visibleBrokerIds.has(b.id)),
      assignments: coverage.assignments.filter((a) => visibleBrokerIds.has(a.brokerId)),
    }
  }, [coverage, focusedBrokerId, rollUp])

  const focusedBroker = focusedBrokerId
    ? coverage.brokers.find((b) => b.id === focusedBrokerId)
    : null

  // ── Lazy-loaded data for the polygon map ──
  const { data: allCountries = [] } = useQuery<Territory[]>({
    queryKey: ['territories', 'all-countries'],
    queryFn: () => apiFetch<Territory[]>('/territories?kind=country'),
    staleTime: Infinity,
  })

  const rollup = useMemo(
    () => rollupAssignments(focusedCoverage, rollUp),
    [focusedCoverage, rollUp],
  )
  // No focus → show every country with any broker presence (rollup roll-up).
  // Focus → show only the *finest* level the focused broker is responsible for,
  // expanding continent-level assignments into their countries.
  const countryPaint = useMemo(
    () =>
      focusedBrokerId
        ? computeCountryLeafPaint(focusedCoverage, rollUp, allCountries)
        : rollup.countries,
    [focusedCoverage, focusedBrokerId, rollUp, rollup.countries, allCountries],
  )
  const { data: topology } = useQuery<Topology>({
    queryKey: ['world-atlas-topology'],
    queryFn: async () => {
      const r = await fetch(WORLD_ATLAS_URL)
      if (!r.ok) throw new Error(`World atlas fetch failed: ${r.status}`)
      return r.json() as Promise<Topology>
    },
    staleTime: Infinity,
  })

  const countryByName = useMemo(() => {
    const m = new Map<string, Territory>()
    for (const c of allCountries) m.set(c.name, c)
    return m
  }, [allCountries])

  const radiusAssignments = useMemo(
    () => focusedCoverage.assignments.filter((a) => a.radiusCenterLat != null && a.radiusCenterLng != null && a.radiusKm != null),
    [focusedCoverage.assignments],
  )

  const activityByCountry = coverage.activityByCountry ?? {}
  const fanDemandPins = coverage.fanDemandPins ?? []

  const fanGapCountries = useMemo(() => {
    const gaps: Array<{ name: string; fanRequests: number }> = []
    for (const c of allCountries) {
      const brokerCount = countryPaint[c.id]?.length ?? 0
      const activity = lookupActivity(activityByCountry, c.name)
      const status = deriveCountryStatus(brokerCount, activity)
      if (status === 'fan_gap' && activity) {
        gaps.push({ name: c.name, fanRequests: activity.fanRequests })
      }
    }
    return gaps.sort((a, b) => b.fanRequests - a.fanRequests)
  }, [allCountries, countryPaint, activityByCountry])

  const sellingCountries = useMemo(() => {
    let count = 0
    for (const c of allCountries) {
      const activity = lookupActivity(activityByCountry, c.name)
      if (activity && (activity.confirmedShows > 0 || activity.ticketsSold > 0)) count++
    }
    return count
  }, [allCountries, activityByCountry])

  // ── Map ──
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<L.Map | null>(null)

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return
    // Initial view: Greenland's tip down to the foot of Chile. Antarctica is
    // *below the fold* but the user can pan/scroll down to reach it because
    // maxBounds still includes the full world.
    const inhabitedView: L.LatLngBoundsLiteral = [
      [-56, -170],
      [83, 180],
    ]
    const worldBounds: L.LatLngBoundsLiteral = [
      [-90, -180],
      [90, 180],
    ]
    const map = L.map(mapRef.current, {
      worldCopyJump: false,
      minZoom: 1,
      maxBounds: worldBounds,
      maxBoundsViscosity: 1.0,
    })
    map.fitBounds(inhabitedView, { animate: false, padding: [0, 0] })
    // Carto Light — English labels, free for non-commercial. If volume
    // grows we'll need an attribution-friendly commercial source.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      maxZoom: 8,
      noWrap: true,
      bounds: worldBounds,
    }).addTo(map)
    mapInstance.current = map
    setTimeout(() => map.invalidateSize(), 100)

    const ro = new ResizeObserver(() => {
      map.invalidateSize()
      map.fitBounds(inhabitedView, { animate: false, padding: [0, 0] })
    })
    ro.observe(mapRef.current)

    return () => {
      ro.disconnect()
      map.remove()
      mapInstance.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapInstance.current
    if (!map || !topology) return
    // Clear previous overlays (keep base tile layer)
    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer)
    })

    const fcRaw = topoFeature(
      topology,
      topology.objects.countries as GeometryCollection,
    ) as FeatureCollection<Geometry, CountryFeatureProps>

    // Drop polygons that cross the antimeridian — they render as horizontal
    // stripes across the world in Web Mercator. Russia is the main casualty;
    // logged as a follow-up so we can split it cleanly later.
    const fc: FeatureCollection<Geometry, CountryFeatureProps> = {
      type: 'FeatureCollection',
      features: fcRaw.features.filter((f) => {
        const lngs: number[] = []
        const walk = (coords: unknown): void => {
          if (typeof coords === 'number') return
          if (Array.isArray(coords)) {
            if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
              lngs.push(coords[0])
            } else {
              for (const c of coords) walk(c)
            }
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        walk((f.geometry as any).coordinates)
        if (lngs.length === 0) return true
        const span = Math.max(...lngs) - Math.min(...lngs)
        return span <= 180
      }),
    }

    function styleFor(feature?: Feature<Geometry, CountryFeatureProps>) {
      const rawName = feature?.properties.name ?? ''
      const name = normaliseCountryName(rawName)
      const country = countryByName.get(name)
      const brokerCount = country ? countryPaint[country.id]?.length ?? 0 : 0
      const activity = country ? lookupActivity(activityByCountry, country.name) : undefined
      const status = deriveCountryStatus(brokerCount, activity)

      if (mapMode === 'responsibility') {
        return {
          weight: 0.5,
          color: '#1f2937',
          opacity: 0.4,
          fillColor:
            brokerCount === 0 ? '#fecaca' : brokerCount === 1 ? '#a7f3d0' : brokerCount <= 3 ? '#34d399' : '#059669',
          fillOpacity: 0.55,
        }
      }

      if (mapMode === 'fan_gap') {
        const isGap = status === 'fan_gap'
        return {
          weight: isGap ? 1 : 0.5,
          color: isGap ? '#b91c1c' : '#9ca3af',
          opacity: 0.5,
          fillColor: isGap ? '#fca5a5' : '#f3f4f6',
          fillOpacity: isGap ? 0.75 : 0.35,
        }
      }

      // selling activity mode
      return {
        weight: 0.5,
        color: '#1f2937',
        opacity: 0.4,
        fillColor: STATUS_FILL[status],
        fillOpacity: status === 'idle' ? 0.35 : 0.6,
      }
    }

    L.geoJSON(fc, {
      style: (f) => styleFor(f as Feature<Geometry, CountryFeatureProps>),
      onEachFeature: (feature, layer) => {
        const f = feature as Feature<Geometry, CountryFeatureProps>
        const rawName = f.properties.name
        const name = normaliseCountryName(rawName)
        const country = countryByName.get(name)
        const badges = country ? countryPaint[country.id] ?? [] : []
        const activity = country ? lookupActivity(activityByCountry, country.name) : undefined
        const status = country ? deriveCountryStatus(badges.length, activity) : 'idle'
        const agentList = badges.length
          ? badges
              .map((b) => `${b.broker.company || b.broker.name}${b.viaBrokerId ? ' (sub-agent)' : ''}`)
              .join('<br/>')
          : '<i>No agent assigned</i>'
        const activityLine = activity
          ? `<br/><span style="font-size:11px">Shows: ${activity.confirmedShows} · Pipeline: ${activity.activeInquiries} · Fan requests: ${activity.fanRequests}</span>`
          : ''
        layer.bindTooltip(
          `<b>${rawName}</b><br/>${agentList}${activityLine}<br/><i>${status.replace('_', ' ')}</i>`,
          { sticky: true },
        )
      },
    }).addTo(map)

    // Radius assignments — paint as purple circles
    for (const a of radiusAssignments) {
      const broker = focusedCoverage.brokers.find((b) => b.id === a.brokerId)
      const label = a.radiusLabel || `${a.radiusKm}km`
      L.circle([a.radiusCenterLat!, a.radiusCenterLng!], {
        radius: a.radiusKm! * 1000,
        color: '#7c3aed',
        weight: 2,
        fillColor: '#a78bfa',
        fillOpacity: 0.25,
      })
        .addTo(map)
        .bindTooltip(
          `<b>${broker?.company || broker?.name || 'Broker'}</b><br/>${label}${
            a.contactName ? `<br/>${a.contactName}` : ''
          }${a.contactEmail ? `<br/>${a.contactEmail}` : ''}`,
          { sticky: true },
        )
    }

    // Sub-country named territories (subdivision / locality) — paint as
    // teal circles at the cached centroid, sized by the per-assignment
    // override or the territory's defaultRadiusKm.
    for (const a of focusedCoverage.assignments) {
      const t = a.territory
      if (!t || (t.kind !== 'subdivision' && t.kind !== 'locality')) continue
      if (t.centroidLat == null || t.centroidLng == null) continue
      const radiusKm = a.radiusKmOverride ?? t.defaultRadiusKm ?? (t.kind === 'locality' ? 30 : 200)
      const broker = focusedCoverage.brokers.find((b) => b.id === a.brokerId)
      const tooltip = [
        `<b>${broker?.company || broker?.name || 'Broker'}</b>`,
        `${t.name} (${radiusKm}km)`,
        a.contactName ? a.contactName : null,
        a.contactEmail ? a.contactEmail : null,
      ]
        .filter(Boolean)
        .join('<br/>')
      L.circle([t.centroidLat, t.centroidLng], {
        radius: radiusKm * 1000,
        color: '#0d9488',
        weight: 1.5,
        fillColor: '#5eead4',
        fillOpacity: 0.2,
      })
        .addTo(map)
        .bindTooltip(tooltip, { sticky: true })
    }

    // Fan location overlay — city pins from Velvet Rope / fan demand sync
    if (showFanOverlay && fanDemandPins.length > 0) {
      for (const pin of fanDemandPins) {
        const icon = makeCountIcon(FAN_PIN_COLOUR, pin.requestCount)
        const samples = pin.sampleComments
          .map(
            (c) =>
              `&ldquo;${c.text.replace(/</g, '&lt;')}&rdquo;`
              + (c.commenterName ? ` &mdash; ${c.commenterName}` : ''),
          )
          .join('<br/>')
        L.marker([pin.lat, pin.lng], { icon })
          .addTo(map)
          .bindPopup(
            `<strong>${pin.city}${pin.country ? `, ${pin.country}` : ''}</strong><br/>`
            + `<b>${pin.requestCount}</b> fan${pin.requestCount === 1 ? '' : 's'} want a show here<br/>`
            + (samples ? `<hr style="margin:6px 0"/>${samples}` : ''),
          )
      }
    }

    // Reverse query — click anywhere on the map → "Who covers this point?"
    map.off('click')
    map.on('click', (evt: L.LeafletMouseEvent) => {
      const hits: string[] = []

      // Country polygons — bounds-contains is a coarse but useful first pass
      const fcLayer = (map as L.Map & { _layers?: Record<string, L.Layer> })._layers
      if (fcLayer) {
        for (const layerId of Object.keys(fcLayer)) {
          const layer = fcLayer[layerId]!
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const feature = (layer as any).feature as Feature<Geometry, CountryFeatureProps> | undefined
          if (feature && layer instanceof L.Path && typeof (layer as L.Polygon).getBounds === 'function') {
            const b = (layer as L.Polygon).getBounds()
            if (b.contains(evt.latlng)) {
              const name = normaliseCountryName(feature.properties.name)
              const country = countryByName.get(name)
              const badges = country ? countryPaint[country.id] ?? [] : []
              for (const b of badges) {
                hits.push(`${b.broker.company || b.broker.name}${b.viaBrokerId ? ' (via descendant)' : ''} — ${feature.properties.name}`)
              }
            }
          }
          // Circles (radius + sub-country)
          if (layer instanceof L.Circle) {
            const center = layer.getLatLng()
            const r = layer.getRadius()
            if (evt.latlng.distanceTo(center) <= r) {
              const tooltip = (layer as L.Circle).getTooltip()
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const html = tooltip ? (tooltip.getContent() as string) ?? '' : ''
              hits.push(html.replace(/<[^>]+>/g, ' · ').replace(/\s+/g, ' ').trim())
            }
          }
        }
      }

      const body = hits.length
        ? `<div style="max-width:240px"><b>Who covers this point</b><br/>${hits.map((h) => `· ${h}`).join('<br/>')}</div>`
        : '<i>No broker covers this point</i>'
      L.popup({ closeButton: true, maxWidth: 260 })
        .setLatLng(evt.latlng)
        .setContent(body)
        .openOn(map)
    })
  }, [topology, countryByName, countryPaint, radiusAssignments, focusedCoverage.brokers, focusedCoverage.assignments, mapMode, showFanOverlay, fanDemandPins, activityByCountry])

  // ── Summary ──
  const continentTotal = continents.length
  const continentCovered = continents.filter((c) => (rollup.continents[c.id]?.length ?? 0) > 0).length

  return (
    <div className="space-y-4">
      {fanGapCountries.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">
                {fanGapCountries.length} market{fanGapCountries.length === 1 ? '' : 's'} with fan demand but no ticket agent
              </p>
              <p className="mt-1 text-xs text-red-800">
                {fanGapCountries
                  .slice(0, 6)
                  .map((g) => `${g.name} (${g.fanRequests})`)
                  .join(' · ')}
                {fanGapCountries.length > 6 ? ` · +${fanGapCountries.length - 6} more` : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {focusedBroker && (
        <div className="flex items-center gap-3 rounded-md border border-ovation-ink bg-ovation-ink/5 px-3 py-2 text-sm">
          <span className="font-medium">
            Focused on {focusedBroker.company || focusedBroker.name}
          </span>
          <span className="text-xs text-ovation-muted">
            (click again or press Clear to see all brokers)
          </span>
          <button
            type="button"
            onClick={onClearFocus}
            className="ml-auto rounded-md border border-ovation-ink/15 px-2 py-0.5 text-xs hover:bg-white"
          >
            Clear focus
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Map — left ~70% on lg+ */}
        <div className="lg:col-span-8">
          <div className="relative h-[520px] overflow-hidden rounded-lg border border-ovation-ink/10 bg-white">
            <div ref={mapRef} className="absolute inset-0" />
            <div className="absolute right-2 top-2 z-[400] rounded-md border border-ovation-ink/10 bg-white/95 p-2 text-[10px] shadow-md max-w-[140px]">
              <div className="mb-1 font-medium uppercase tracking-wider text-ovation-muted">
                {mapMode === 'responsibility' ? 'Agents' : mapMode === 'fan_gap' ? 'Fan gaps' : 'Selling'}
              </div>
              {mapMode === 'responsibility' ? (
                <>
                  <LegendSwatch colour="#fecaca" label="0 — gap" />
                  <LegendSwatch colour="#a7f3d0" label="1 agent" />
                  <LegendSwatch colour="#34d399" label="2–3 agents" />
                  <LegendSwatch colour="#059669" label="4+ agents" />
                </>
              ) : mapMode === 'fan_gap' ? (
                <>
                  <LegendSwatch colour="#fca5a5" label="Fans, no agent" />
                  <LegendSwatch colour="#f3f4f6" label="Other" />
                </>
              ) : (
                <>
                  <LegendSwatch colour={STATUS_FILL.selling} label="Selling" />
                  <LegendSwatch colour={STATUS_FILL.pipeline} label="Pipeline only" />
                  <LegendSwatch colour={STATUS_FILL.fan_gap} label="Fans, no agent" />
                  <LegendSwatch colour={STATUS_FILL.covered_idle} label="Agent, quiet" />
                  <LegendSwatch colour={STATUS_FILL.idle} label="No activity" />
                </>
              )}
              {showFanOverlay && (
                <div className="mt-2 border-t border-ovation-ink/10 pt-2">
                  <LegendSwatch colour={FAN_PIN_COLOUR} label="Fan city pin" />
                </div>
              )}
              <div className="mt-2 border-t border-ovation-ink/10 pt-2">
                <LegendSwatch colour="#5eead4" label="Sub-country" />
                <LegendSwatch colour="#a78bfa" label="Radius zone" />
              </div>
            </div>
          </div>
        </div>

        {/* Side panel — right ~30% on lg+ */}
        <aside className="space-y-3 lg:col-span-4">
          <BrokerAgentHierarchy
            coverage={focusedCoverage}
            rollUp={rollUp}
            focusedBrokerId={focusedBrokerId}
            {...(onFocusBroker ? { onFocusBroker } : {})}
          />

          <div className="grid grid-cols-1 gap-2">
            <SummaryStat
              label="Markets selling"
              value={String(sellingCountries)}
              {...(sellingCountries > 0 ? { tone: 'good' as const } : {})}
            />
            <SummaryStat
              label="Fan gaps (no agent)"
              value={String(fanGapCountries.length)}
              tone={fanGapCountries.length > 0 ? 'warn' : 'good'}
            />
            <SummaryStat
              label="Continents covered"
              value={`${continentCovered} / ${continentTotal}`}
              tone={continentCovered === continentTotal ? 'good' : 'warn'}
            />
            <SummaryStat
              label="Agents assigned"
              value={`${focusedCoverage.brokers.filter((b) => !!focusedCoverage.assignments.find((a) => a.brokerId === b.id)).length} / ${focusedCoverage.brokers.length}`}
            />
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-ovation-ink/10 bg-white">
            <div className="divide-y divide-ovation-ink/10">
              {continents.map((c) => (
                <ContinentBlock
                  key={c.id}
                  continent={c}
                  rollup={rollup}
                  gapsOnly={gapsOnly}
                  countryPaint={countryPaint}
                  activityByCountry={activityByCountry}
                />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

function BrokerAgentHierarchy({
  coverage,
  rollUp,
  focusedBrokerId,
  onFocusBroker,
}: {
  coverage: CoveragePayload
  rollUp: boolean
  focusedBrokerId: string | null
  onFocusBroker?: (brokerId: string) => void
}) {
  const roots = coverage.brokers.filter(
    (b) => !b.parentId || !coverage.brokers.some((p) => p.id === b.parentId),
  )

  function assignmentsForBroker(brokerId: string): Assignment[] {
    const descendants = rollUp ? buildDescendantClosure(coverage.childrenOf) : null
    const ids = rollUp ? [...descendants!(brokerId)] : [brokerId]
    return coverage.assignments.filter((a) => ids.includes(a.brokerId))
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Users className="h-4 w-4 text-ovation-muted" />
        Agent hierarchy
      </div>
      {roots.length === 0 ? (
        <p className="text-xs text-ovation-muted">No ticket agents yet.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {roots.map((root) => (
            <HierarchyNode
              key={root.id}
              broker={root}
              depth={0}
              coverage={coverage}
              rollUp={rollUp}
              focusedBrokerId={focusedBrokerId}
              {...(onFocusBroker ? { onFocusBroker } : {})}
              assignmentsForBroker={assignmentsForBroker}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function HierarchyNode({
  broker,
  depth,
  coverage,
  rollUp,
  focusedBrokerId,
  onFocusBroker,
  assignmentsForBroker,
}: {
  broker: BrokerLite
  depth: number
  coverage: CoveragePayload
  rollUp: boolean
  focusedBrokerId: string | null
  onFocusBroker?: (brokerId: string) => void
  assignmentsForBroker: (id: string) => Assignment[]
}) {
  const childIds = coverage.childrenOf[broker.id] ?? []
  const children = childIds
    .map((id) => coverage.brokers.find((b) => b.id === id))
    .filter(Boolean) as BrokerLite[]
  const assignments = assignmentsForBroker(broker.id)
  const isFocused = focusedBrokerId === broker.id
  const tierLabel = broker.tier === 'sub_broker' ? 'Sub-agent' : broker.tier.replace(/_/g, ' ')

  return (
    <li>
      <button
        type="button"
        disabled={!onFocusBroker}
        onClick={() => onFocusBroker?.(broker.id)}
        className={`w-full rounded-md border px-2 py-1.5 text-left transition-colors ${
          isFocused
            ? 'border-ovation-ink bg-ovation-ink/10'
            : 'border-ovation-ink/10 hover:bg-ovation-ink/5'
        } ${!onFocusBroker ? 'cursor-default' : ''}`}
        style={{ marginLeft: depth * 12 }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium truncate">{broker.company || broker.name}</span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
              TIER_COLOUR[broker.tier] ?? 'bg-slate-100 text-slate-800'
            }`}
          >
            {tierLabel}
          </span>
        </div>
        {assignments.length > 0 ? (
          <p className="mt-1 text-[11px] text-ovation-muted line-clamp-2">
            {assignments.slice(0, 4).map(describeTerritoryAssignment).join(' · ')}
            {assignments.length > 4 ? ` · +${assignments.length - 4}` : ''}
          </p>
        ) : (
          <p className="mt-1 text-[11px] text-amber-700">No territory assigned</p>
        )}
      </button>
      {children.length > 0 && (
        <ul className="mt-1 space-y-1">
          {children.map((child) => (
            <HierarchyNode
              key={child.id}
              broker={child}
              depth={depth + 1}
              coverage={coverage}
              rollUp={rollUp}
              focusedBrokerId={focusedBrokerId}
              {...(onFocusBroker ? { onFocusBroker } : {})}
              assignmentsForBroker={assignmentsForBroker}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function ContinentBlock({
  continent,
  rollup,
  gapsOnly,
  countryPaint,
  activityByCountry,
}: {
  continent: Territory
  rollup: AssignmentsByTerritory
  gapsOnly: boolean
  countryPaint: Record<string, BrokerBadge[]>
  activityByCountry: Record<string, CountryActivity>
}) {
  const [expanded, setExpanded] = useState(false)
  const badges = rollup.continents[continent.id] ?? []
  const isGap = badges.length === 0

  // All hooks must be called every render — early-return for gaps-only AFTER.
  const countriesQ = useQuery<Territory[]>({
    queryKey: ['territories', 'countries', continent.id],
    queryFn: () => apiFetch<Territory[]>(`/territories?parentId=${continent.id}`),
    enabled: expanded,
  })

  if (gapsOnly && !isGap) return null

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-ovation-ink/5"
      >
        <div className="flex items-center gap-2">
          {isGap && <AlertTriangle className="h-4 w-4 text-red-600" />}
          <span className="font-display text-base">{continent.name}</span>
          <span className="text-xs text-ovation-muted">
            {badges.length} broker{badges.length === 1 ? '' : 's'}
          </span>
        </div>
        <span className="text-xs text-ovation-muted">{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className="bg-ovation-paper px-4 py-3">
          {badges.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-ovation-muted">
                Continent-level
              </div>
              <BrokerBadgeList badges={badges} />
            </div>
          )}
          {countriesQ.data && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
                Countries
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3">
                {countriesQ.data.map((country) => {
                  const countryBadges = rollup.countries[country.id] ?? []
                  const paintBadges = countryPaint[country.id] ?? []
                  const brokerCount = paintBadges.length || countryBadges.length
                  const activity = lookupActivity(activityByCountry, country.name)
                  const status = deriveCountryStatus(brokerCount, activity)
                  const countryGap = brokerCount === 0
                  const showGap = gapsOnly && (countryGap || status === 'fan_gap')
                  if (gapsOnly && !showGap) return null
                  return (
                    <div
                      key={country.id}
                      className={`rounded border px-2 py-1.5 text-xs ${
                        status === 'fan_gap'
                          ? 'border-red-300 bg-red-50'
                          : status === 'selling'
                          ? 'border-emerald-200 bg-emerald-50'
                          : countryGap
                          ? 'border-red-200 bg-red-50/50'
                          : 'border-ovation-ink/10 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{country.name}</span>
                        <StatusPill status={status} />
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-2 text-[10px] text-ovation-muted">
                        <span>Agents: {brokerCount}</span>
                        {activity && (
                          <>
                            <span>Shows: {activity.confirmedShows}</span>
                            <span>Pipe: {activity.activeInquiries}</span>
                            <span>Fans: {activity.fanRequests}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatusPill({ status }: { status: CountryStatus }) {
  const labels: Record<CountryStatus, string> = {
    selling: 'Selling',
    pipeline: 'Pipeline',
    fan_gap: 'Fan gap',
    covered_idle: 'Agent',
    idle: 'Quiet',
  }
  const colours: Record<CountryStatus, string> = {
    selling: 'bg-emerald-100 text-emerald-800',
    pipeline: 'bg-amber-100 text-amber-800',
    fan_gap: 'bg-red-100 text-red-800',
    covered_idle: 'bg-teal-100 text-teal-800',
    idle: 'bg-slate-100 text-slate-600',
  }
  return (
    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${colours[status]}`}>
      {labels[status]}
    </span>
  )
}

function BrokerBadgeList({ badges }: { badges: BrokerBadge[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {badges.map((b, i) => (
        <li
          key={`${b.broker.id}-${b.viaBrokerId ?? 'self'}-${i}`}
          className={`inline-flex items-center gap-1 rounded-full border border-ovation-ink/15 px-2 py-0.5 text-xs ${
            TIER_COLOUR[b.broker.tier] ?? 'bg-slate-100 text-slate-800'
          }`}
        >
          <MapPin className="h-3 w-3" />
          <span>{b.broker.company || b.broker.name}</span>
          {b.viaBrokerId && (
            <span className="text-[10px] opacity-75">(via descendant)</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function LegendSwatch({ colour, label }: { colour: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm border border-ovation-ink/30"
        style={{ backgroundColor: colour }}
      />
      <span>{label}</span>
    </div>
  )
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'good' | 'warn'
}) {
  const toneClass =
    tone === 'good'
      ? 'border-emerald-200 bg-emerald-50'
      : tone === 'warn'
      ? 'border-amber-200 bg-amber-50'
      : 'border-ovation-ink/10 bg-white'
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider text-ovation-muted">{label}</div>
      <div className="mt-0.5 font-display text-xl">{value}</div>
    </div>
  )
}

